import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { PublicationService } from '../../../services/publication.service';
import { HttpClient } from '@angular/common/http';
import { AuthenticationService } from '../../../services/authentication.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ToCodePipe } from '../../../pipes/toCode.pipe';
import { AbstractDetailsComponent } from '../../../components/shared/abstract-details.component';
import { NgForm } from '@angular/forms';
import { SapiEntity, SapiService } from '../../../services/sapi.service';
import { Publication } from '../../../interfaces/publication';
import { BbTableService, TableRow } from '../../../services/bb-table.service';
import { Playlist } from '../../../interfaces/playlist';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { WorklistService } from '../../../services/worklist.service';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { Mediaclip } from 'app/interfaces/mediaclip';
import { ModalDefinition } from 'app/services/overlay.service';
import { Project } from 'app/interfaces/project';

@Component({
	selector: 'playlist-details',
	templateUrl: './playlist-details.component.html',
	styleUrls: ['./playlist-details.component.scss'],
	providers: [BbTableService]
})

export class PlaylistDetailsComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {

	@ViewChild('formRef', { static: true }) form: NgForm;

	activeTab: string;
	baseUrl: string;
	subscriptions: Subscription;
	rows: TableRow[] = [];
	playlist: Playlist;
	playTime = 0;
	rowsTotal = 0;
	filterSort: any = {};
	sortPropertyOptions: any[];
	customFields: any[];
	queryFromTableService: string;
	bbTableServiceAttached: boolean;
	showProjectPlaytimeWarning: boolean;

	constructor(protected route: ActivatedRoute,
		protected location: Location,
		protected publicationService: PublicationService,
		protected sapiService: SapiService,
		protected http: HttpClient,
		protected authenticationService: AuthenticationService,
		protected sanitizer: DomSanitizer,
		protected toCodePipe: ToCodePipe,
		protected bbTableService: BbTableService,
		protected translate: TranslateService,
		protected worklistService: WorklistService,
		protected customFieldService: CustomFieldsService) {

		super(route, location);

		this.entityConfiguration = {
			name: 'cliplist',
			parentPath: '/library/playlists',
			basePath: '/library/playlist/',
			entityTranslation: 'playlist',
			tabItems: [{
				title: this.translate.instant('GENERAL'),
				routerLink: ['/library/playlist', 'general'],
				icon: 'tags'
			},
			{
				title: this.translate.instant('RSS_CONFIGURATION'),
				routerLink: ['/library/playlist', 'rss'],
				icon: 'cogs'
			},
			{
				title: this.translate.instant('EMBED'),
				routerLink: ['/library/playlist', 'embed'],
				icon: 'code'
			}]
		};
		this.subscriptions = new Subscription();

		this.subscriptions.add(
			this.publicationService.getData().subscribe((publication: Publication) => {
				if (publication) {
					this.baseUrl = publication.baseurl;
				}
			})
		);

		this.sortPropertyOptions = [
			{ value: 'publisheddate', label: translate.instant('PUBLISHED_DATE') },
			{ value: 'createddate', label: translate.instant('CREATED_DATE') },
			{ value: 'updateddate', label: translate.instant('UPDATED_DATE') },
			{ value: 'title', label: translate.instant('TITLE') },
			{ value: 'mediatype', label: translate.instant('MEDIATYPE') },
			{ value: 'sourcetype', label: translate.instant('SOURCETYPE') },
			{ value: 'status', label: translate.instant('STATUS') },
			{ value: 'views', label: this.translate.instant('VIEWS') }
		];
	}

	ngOnInit() {
		super.ngOnInit();

		this.playlist = this.entity as Playlist;
		if (this.isNew) {
			if (this.activeId === 'new_static') {
				this.playlist.listtype = 'static';
				this.filterSort = { property: 'publisheddate', direction: 'desc' };
			} else {
				this.playlist.listtype = 'dynamic';
				this.playlist.filterType = 'filters';
			}
			this.playlist.items = [];
			this.playlist.sort = 'publisheddate desc';

			this.attachToBbTableService();
		}

		this.setBaseFilterSetSmartList(this.activeId);
		this.queryFromTableService = '';
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	setBaseFilterSetSmartList(id: string): void {
		// used for dynamic (smart) lists -> row
		this.bbTableService
			.setEntity('search')
			.setBaseFilterSet(
				[
					{
						filters: [{
							type: 'search',
							field: 'type',
							operator: 'is',
							value: ['Project', 'MediaClip', 'MediaClipList']
						}]
					},
					{
						filters: [{
							type: 'search',
							field: 'usetype',
							operator: 'doesNotContainAnyOf',
							value: ['commercial', 'social']
						}]
					}
				]
			)
			.setFilterQuery(`*:* AND -id:${id}`);
	}

	save(force: boolean, extraHeaders?: any, saveDirectly: boolean = false, publish?: boolean): Observable<any> {
		const isNew = this.activeId.includes('new');
		const saveObservable = super.save(force, extraHeaders, saveDirectly, publish);

		saveObservable.subscribe(() => {
			this.playlist = this.entity as Playlist;
			if (isNew && this.entity.id) {
				this.setBaseFilterSetSmartList(this.entity.id);
			}
		});
		return saveObservable;
	}

	load(id: string): Observable<SapiEntity> {
		const loadObservable = super.load(id);
		loadObservable.subscribe(() => {
			if (this.entity) {
				this.playlist = (this.entity as Playlist);

				if (this.playlist.listtype === 'static') {
					this.filterSort = {
						property: 'publisheddate',
						direction: 'desc'
					};

					this.customFields = this.customFieldService.getCustomFields('mediaclip');
					for (let i = 0; i < this.customFields.length; i++) {
						this.sortPropertyOptions.push({ value: this.customFields[i].name, label: this.customFields[i].label.toLowerCase() });
					}

					this.calculatePlaytime();
				}

				if (this.playlist.limit) {
					this.bbTableService.addExtraQueryParameter('mediaclipLengthLimit', this.playlist.limit.toString(10));
				}

				this.attachToBbTableService();
			}
		});

		return loadObservable;
	}

	showPreview(clip: Mediaclip | Project) {
		const modal: ModalDefinition = {
			icon: 'film',
			title: (clip.title || clip.originalfilename),
			component: {
				name: 'playlist-preview',
				inputs: {
					[clip.type === 'Project' ? 'project' : 'mediaclip']: clip,
					configuration: {
						autoPlay: true,
						showVideo: true,
						alwaysShow: true
					}
				}
			}
		};

		this.overlayService.createModal(modal);
	}

	attachToBbTableService() {
		if (!this.bbTableServiceAttached && this.playlist && this.playlist.listtype === 'dynamic') {
			let property = 'publisheddate';
			let direction = 'desc';

			if (this.playlist.sort) {
				const sortArray = this.playlist.sort.split(' ');
				if (sortArray && sortArray.length > 1) {
					property = sortArray[0];
					direction = sortArray[1];
				}
			}

			this.bbTableService
				.setConfigurable(false)
				.setFacetField('length_int')
				.setLimit(15)
				.sort(property, direction)
				.updateSort(null, true)
				.setMaxTotalAmount(this.playlist.limit);

			this.subscriptions.add(this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => {
				if (rows) {
					this.rows = rows;
				}
			}));

			this.subscriptions.add(this.bbTableService.facetFieldsObservable.subscribe((facets: any) => {
				if ((this.playlist.isSlideshow === true || this.playlist.isSlideshow === 'true') && this.playlist.slideDuration) {
					this.playTime = Number(this.playlist.slideDuration) * this.playlist.items.length;
				} else if (facets && facets.length) {
					this.playTime = 0;
					if (facets.length.length) {
						const facetArray = facets.length;
						while (facetArray.length > 1) {
							const length = facetArray.shift();
							const times = facetArray.shift();
							this.playTime += (length * times);
						}
					}
				}
			}));

			this.subscriptions.add(this.bbTableService.totalChangedObservable.subscribe((total: number) => {
				if (total) {
					this.rowsTotal = total;
				} else {
					this.rowsTotal = 0;
				}
			}));

			this.subscriptions.add(this.bbTableService.queryObservable.subscribe((query: string) => {
				if (query) {
					this.queryFromTableService = query;
				}
			}));

			if (this.isNew) {
				this.bbTableService.getRows(true);
			}

			this.bbTableServiceAttached = true;
		}
	}

	calculatePlaytime() {
		this.playTime = 0;
		if (this.playlist && this.playlist.items && this.playlist.items.length) {
			if ((this.playlist.isSlideshow === true || this.playlist.isSlideshow === 'true') && this.playlist.slideDuration) {
				this.playTime = Number(this.playlist.slideDuration) * this.playlist.items.length;
			} else {
				for (let i = 0; i < this.playlist.items.length; i++) {
					if (this.playlist.items[i]) {
						this.playTime += typeof this.playlist.items[i].length === 'number' ? this.playlist.items[i].length : 0;
					}
				}
			}
		}
		this.checkContainsProjects();
	}

	onPlaylistFilterChange(playlist: Playlist) {
		let limit = '-1';
		if (playlist.limit) {
			limit = playlist.limit.toString();
		}

		if ((this.playlist.isSlideshow === true || this.playlist.isSlideshow === 'true') && this.playlist.slideDuration) {
			this.playTime = Number(this.playlist.slideDuration) * this.playlist.items.length;
		}
		this.bbTableService
			.addExtraQueryParameter('mediaclipLengthLimit', limit)
			.setFilterSet(playlist.filters)
			.setFilterQuery(`*:* AND -id:${playlist.id}`)
			.useOverlayLoading(false)
			.getRows(true);
	}

	onPlaylistQueryChange(playlist: Playlist) {
		let limit = '-1';
		if (playlist.limit) {
			limit = playlist.limit.toString();
		}
		this.bbTableService
			.setFilterSet(null)
			.addExtraQueryParameter('mediaclipLengthLimit', limit)
			.setFilterQuery(playlist.query)
			.getRows(true);
	}

	sortOrderChange(sortorder: any) {
		this.bbTableService
			.updateSortDirection(sortorder.direction)
			.updateSort(sortorder.property, true)
			.getRows(true);
	}

	addedSelectedClips(selectedClips: any) {
		this.form.form.markAsDirty();
		for (let i = 0; i < selectedClips.length; i++) {
			this.playlist.items.push(selectedClips[i].data);
		}
		this.calculatePlaytime();
	}

	unlinkItem(rowToUnlink: Mediaclip | Project, rowIndex: number) {
		this.form.form.markAsDirty();
		if (rowIndex !== -1) {
			this.playlist.items.splice(rowIndex, 1);
		}
		this.calculatePlaytime();
	}

	manualSort() {
		const property = this.filterSort.property;
		this.playlist.items.sort((a: any, b: any) => {
			a[property] = typeof a[property] === 'string' ? a[property].toLowerCase() : a[property];
			b[property] = typeof b[property] === 'string' ? b[property].toLowerCase() : b[property];

			if (!a[property] && !b[property]) {
				return 0;
			} else if (!a[property]) {
				return this.filterSort.direction === 'asc' ? -1 : 1;
			} else if (!b[property]) {
				return this.filterSort.direction === 'asc' ? 1 : -1;
			} else if (a[property] < b[property]) {
				return this.filterSort.direction === 'asc' ? -1 : 1;
			} else {
				return this.filterSort.direction === 'asc' ? 1 : -1;
			}
		});
	}

	reorder(event: CdkDragDrop<string[]>) {
		moveItemInArray(this.playlist.items, event.previousIndex, event.currentIndex);
		this.form.form.markAsDirty();
	}

	addToWorklist(items: any) {
		const itemsToAdd = [];
		if (!items) {
			return false;
		} else {
			for (let i = 0; i < items.length; i++) {
				itemsToAdd.push({ id: items[i].id, data: items[i] });
			}
			this.worklistService.addItems(itemsToAdd);

			if (!this.worklistService.worklist.isOpen) {
				this.worklistService.toggle();
			}
		}
	}

	trackByIndex(index: number, item: any) {
		return index;
	}

	// display a warning regarding playTime when projects are involved
	private checkContainsProjects(): void {
		if (!this.playlist || !this.playlist.items) {
			this.showProjectPlaytimeWarning = false;
		}

		this.showProjectPlaytimeWarning = this.playlist.items.some(item => item.type === 'Project');
	}
}
